<template>
  <div class="estadisticas-container">
    <div class="estadisticas-content">
      <h2 class="titulo">Dashboard</h2>

      <div class="filtros">
        <div>
          <label>Fecha</label>
          <input type="date" v-model="desde" class="form-control" :max="fechaHoy" />
        </div>
        <div class="form-check">
          <input type="checkbox" v-model="usarRangoFechas" id="checkRango" class="form-check-input" @change="activarRango" />
          <label for="checkRango" class="form-check-label">Buscar por rango</label>
        </div>
        <div v-if="usarRangoFechas">
          <label>Hasta</label>
          <input type="date" v-model="hasta" class="form-control" :min="desde" :max="fechaHoy" @change="obtenerEstadisticas" />
        </div>
      </div>

      <div class="cards">
        <div class="card-item bg-light-pink cursor-pointer" @click="mostrarGraficoIngresos">
          <h5>Ingresos</h5>
          <p class="monto">{{ formatCurrency(datos.ingresos) }}</p>
        </div>
        <div class="card-item bg-light-red cursor-pointer" @click="mostrarGraficoEgresos">
          <h5>Egresos</h5>
          <p class="monto">{{ formatCurrency(datos.egresos) }}</p>
        </div>
        <div class="card-item bg-light-green">
          <h5>Utilidad</h5>
          <p class="monto">{{ formatCurrency(utilidad) }}</p>
        </div>
        <div class="card-item bg-light-blue cursor-pointer" @click="goToEstadisticas">
          <h5>Vestidos Rentados</h5>
          <p class="cantidad">{{ datos.vestidos }}</p>
        </div>
        <div class="card-item bg-light-yellow">
          <h5>Anticipos</h5>
          <p class="monto">{{ formatCurrency(datos.anticipos) }}</p>
        </div>
        <div class="card-item bg-light-gray">
          <h5>Faltante por Pagar</h5>
          <p class="monto">{{ formatCurrency(datos.resto) }}</p>
        </div>
      </div>

      <div class="grafico">
        <BarChart :chart-data="chartData" :chart-options="chartOptions" />
      </div>

      <div v-if="mostrarModalGrafico" class="modal-backdrop" ref="modal">
        <div class="modal-card">
          <div class="modal-header">
            <h4>Detalle de Egresos por Concepto</h4>
            <button class="close" @click="mostrarModalGrafico = false">&times;</button>
          </div>
          <div class="modal-body" ref="graficoEgresos">
            <PieChart :chart-data="graficoEgresosData" :chart-options="pastelOptions" />
          </div>
        </div>
      </div>

      <div v-if="mostrarModalIngresos" class="modal-backdrop" ref="modalIngresos">
        <div class="modal-card">
          <div class="modal-header">
            <h4>Detalle de Ingresos por Concepto</h4>
            <button class="close" @click="mostrarModalIngresos = false">&times;</button>
          </div>
          <div class="modal-body" ref="graficoIngresos">
            <PieChart :chart-data="graficoIngresosData" :chart-options="pastelOptions" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import baseUrl from '../api';
import moment from 'moment-timezone'; // ya está registrado arriba

import { Bar, Pie } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement
} from 'chart.js';
import gsap from 'gsap';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

export default {
  name: 'estadisticas-vue',
  components: {
    BarChart: Bar,
    PieChart: Pie
  },
  data() {
    const zona = moment.tz.guess(); // Detecta la zona horaria del usuario
    const hoy = moment().tz(zona).format("YYYY-MM-DD");
    return {
      desde: hoy,
      hasta: hoy,
      fechaHoy: hoy,
      usarRangoFechas: false,
      datos: {
        ingresos: 0,
        egresos: 0,
        vestidos: 0,
        anticipos: 0,
        resto: 0
      },
      chartData: {
        labels: ['Ingresos', 'Egresos', 'Utilidad', 'Anticipos', 'Faltante'],
        datasets: [
          {
            label: 'Monto en MXN',
            data: [0, 0, 0, 0, 0],
            backgroundColor: ['#f48fb1', '#ef9a9a', '#a5d6a7', '#fff176', '#b0bec5']
          }
        ]
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
          title: { display: true, text: 'Comparativo de Montos' }
        },
        scales: { y: { beginAtZero: true } }
      },
      pastelOptions: {
        responsive: true,
        animation: {
          animateRotate: true,
          animateScale: true,
          duration: 1200,
          easing: 'easeOutQuart'
        },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true }
        }
      },
      mostrarModalGrafico: false,
      mostrarModalIngresos: false,
      graficoEgresosData: {
        labels: [],
        datasets: [
          {
            label: 'Egresos por concepto',
            data: [],
            backgroundColor: [
              '#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#845EC2',
              '#F9F871', '#FFC75F', '#FF9671', '#D65DB1', '#008F7A'
            ]
          }
        ]
      },
      graficoIngresosData: {
        labels: [],
        datasets: [
          {
            label: 'Ingresos por concepto',
            data: [],
            backgroundColor: [
              '#f48fb1', '#90caf9', '#a5d6a7', '#ffcc80', '#ce93d8',
              '#80cbc4', '#ffab91', '#fdd835', '#9fa8da', '#ef9a9a'
            ]
          }
        ]
      }
    };
  },
  computed: {
    utilidad() {
      return this.datos.ingresos - this.datos.egresos;
    }
  },
  methods: {
    goToEstadisticas() {
      this.$router.push('/home/historial');
    },
    async obtenerEstadisticas() {
      try {
        const body = this.usarRangoFechas
          ? { desde: this.desde, hasta: this.hasta }
          : { desde: this.desde, hasta: this.desde };

        const response = await axios.post(`${baseUrl}/api/estadisticas`, body);
        this.datos = response.data;
        this.chartData.datasets[0].data = [
          response.data.ingresos,
          response.data.egresos,
          response.data.ingresos - response.data.egresos,
          response.data.anticipos,
          response.data.resto
        ];
      } catch (error) {
        console.error('Error al obtener estadísticas', error);
      }
    },
    async mostrarGraficoEgresos() {
      try {
        const body = this.usarRangoFechas
          ? { desde: this.desde, hasta: this.hasta }
          : { desde: this.desde, hasta: this.desde };

        const response = await axios.post(`${baseUrl}/api/egresos-conceptos`, body);
        const agrupados = response.data.reduce((acc, eg) => {
          acc[eg.concepto] = (acc[eg.concepto] || 0) + parseFloat(eg.monto);
          return acc;
        }, {});

        this.graficoEgresosData.labels = Object.keys(agrupados);
        this.graficoEgresosData.datasets[0].data = Object.values(agrupados);

        this.mostrarModalGrafico = true;

        this.$nextTick(() => {
          gsap.from(this.$refs.modal, {
            opacity: 0,
            scale: 0.8,
            duration: 0.5,
            ease: 'power2.out'
          });
          gsap.from(this.$refs.graficoEgresos, {
            opacity: 0,
            y: 50,
            duration: 0.8,
            ease: 'power3.out',
            delay: 0.2
          });
        });
      } catch (err) {
        console.error('Error al obtener egresos para gráfico', err);
      }
    },
    async mostrarGraficoIngresos() {
      try {
        const body = this.usarRangoFechas
          ? { desde: this.desde, hasta: this.hasta }
          : { desde: this.desde, hasta: this.desde };

        const response = await axios.post(`${baseUrl}/api/ingresos-conceptos`, body);
        const agrupados = response.data.reduce((acc, ing) => {
          acc[ing.concepto] = (acc[ing.concepto] || 0) + parseFloat(ing.monto);
          return acc;
        }, {});

        this.graficoIngresosData.labels = Object.keys(agrupados);
        this.graficoIngresosData.datasets[0].data = Object.values(agrupados);

        this.mostrarModalIngresos = true;

        this.$nextTick(() => {
          gsap.from(this.$refs.modalIngresos, {
            opacity: 0,
            scale: 0.8,
            duration: 0.5,
            ease: 'power2.out'
          });
          gsap.from(this.$refs.graficoIngresos, {
            opacity: 0,
            y: 50,
            duration: 0.8,
            ease: 'power3.out',
            delay: 0.2
          });
        });
      } catch (err) {
        console.error('Error al obtener ingresos para gráfico', err);
      }
    },
    formatCurrency(valor) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
      }).format(valor || 0);
    },
    activarRango() {
      if (this.usarRangoFechas) {
        this.hasta = this.desde;
      }
      this.obtenerEstadisticas();
    }
  },
  watch: {
    desde() {
      this.obtenerEstadisticas();
    },
    hasta(newVal) {
      if (this.usarRangoFechas && newVal >= this.desde) {
        this.obtenerEstadisticas();
      }
    }
  },
  mounted() {
    this.obtenerEstadisticas();
  }
};
</script>

<style scoped>
.estadisticas-container {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 20px;
}

.estadisticas-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.titulo {
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
}

.filtros {
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}
.h2{ font-size: 28px;
  color: #333;}
.card-item {
  background-color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.card-item h5 {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 10px;
}

.card-item .monto {
  font-size: 1.6rem;
  font-weight: bold;
}

.card-item .cantidad {
  font-size: 2rem;
  font-weight: bold;
}

.bg-light-pink { background-color: #fce4ec; }
.bg-light-red { background-color: #ffebee; }
.bg-light-green { background-color: #e8f5e9; }
.bg-light-blue { background-color: #e3f2fd; }
.bg-light-yellow { background-color: #fffde7; }
.bg-light-gray { background-color: #f5f5f5; }

.grafico {
  max-width: 600px;
  margin: 40px auto;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.grafico canvas {
  width: 100% !important;
  height: auto !important;
  max-height: 400px;
}


@media (max-width: 768px) {
  .estadisticas-content {
    padding: 10px;
  }

  .titulo {
    font-size: 22px;
    margin-bottom: 20px;
  }

  .filtros {
    flex-direction: column;
    gap: 10px;
  }

  .filtros label {
    font-size: 14px;
  }

  .filtros input,
  .filtros button {
    width: 100%;
    font-size: 14px;
  }

  .cards {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .card-item {
    padding: 1rem;
  }

  .card-item h5 {
    font-size: 1rem;
  }

  .card-item .monto,
  .card-item .cantidad {
    font-size: 1.4rem;
  }

  .grafico {
    padding: 10px;
    margin: 30px auto;
  }

  .grafico canvas {
    max-height: 300px;
  }
  .estadisticas-container {

  margin-top: -60px;
}
}

.cursor-pointer {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.cursor-pointer:hover {
  transform: scale(1.03);
}


.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  width: 90%;
  max-width: 400px; /* antes era más grande */
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.15);
}


.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.modal-header h4 {
  font-size: 1.2rem;
  color: #333;
}

.modal-header .close {
  font-size: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  max-height: 360px;
  overflow-y: auto;
  padding: 10px;
}

.modal-body canvas {
  max-width: 160px;
  max-height: 160px;
  margin: 0 auto;
  display: block;
}


@media (max-width: 480px) {
  .modal-card {
    padding: 15px;
    max-width: 95%;
  }
}


</style>
